import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import './TesteIA.css';

const VEREDITOS = [
  { v: 'ok',        emoji: '✅', label: 'Boa',        color: '#2e7d32' },
  { v: 'aceitavel', emoji: '⚠️', label: 'Aceitável', color: '#ed6c02' },
  { v: 'ruim',      emoji: '❌', label: 'Ruim',       color: '#c62828' },
];

export default function TesteIADetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTools, setExpandedTools] = useState({});

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai-testes/runs/${id}`);
      setData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregar();
    const t = setInterval(() => {
      // Auto-refresh enquanto em execução
      if (data?.run?.status === 'em_execucao') carregar();
    }, 5000);
    return () => clearInterval(t);
  }, [carregar, data?.run?.status]);

  const setVeredito = async (resultId, veredito, observacao) => {
    try {
      await axios.patch(`${API_URL}/api/ai-testes/resultados/${resultId}/veredito`, {
        veredito, observacao,
      });
      carregar();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <div className="data-page"><p>Carregando...</p></div>;
  if (!data) return <div className="data-page"><p>Run não encontrado.</p></div>;

  const { run, cases } = data;

  // Estatísticas: por modelo, conta vereditos
  const statsModelo = {};
  for (const c of cases) {
    for (const r of (c.resultados || [])) {
      if (!statsModelo[r.modelo]) statsModelo[r.modelo] = { ok: 0, aceitavel: 0, ruim: 0, sem: 0, tokens: 0, custo: 0, latencia: 0, n: 0, erros: 0 };
      const s = statsModelo[r.modelo];
      if (r.veredito === 'ok') s.ok++;
      else if (r.veredito === 'aceitavel') s.aceitavel++;
      else if (r.veredito === 'ruim') s.ruim++;
      else s.sem++;
      s.tokens += (r.tokens_input || 0) + (r.tokens_output || 0);
      s.custo += Number(r.custo_usd || 0);
      s.latencia += r.latencia_ms || 0;
      s.n++;
      if (r.erro) s.erros++;
    }
  }

  const renderTools = (resultId, tools) => {
    if (!tools || tools.length === 0) return <small style={{ color: '#999' }}>(sem tool calls)</small>;
    const expanded = expandedTools[resultId];
    return (
      <div>
        <button
          className="btn-link"
          style={{ fontSize: 12, padding: 0 }}
          onClick={() => setExpandedTools(s => ({ ...s, [resultId]: !s[resultId] }))}
        >
          {expanded ? '▼' : '▶'} {tools.length} tool call{tools.length > 1 ? 's' : ''}: {tools.map(t => t.name).join(', ')}
        </button>
        {expanded && (
          <pre style={{ background: '#f5f5f5', padding: 6, fontSize: 11, marginTop: 4, maxHeight: 200, overflowY: 'auto', borderRadius: 4 }}>
            {tools.map((t, i) => `[iter ${t.iter}] ${t.name}(${JSON.stringify(t.args)})\n  → ${t.result_resumo}`).join('\n\n')}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="data-page" style={{ maxWidth: '100%' }}>
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <button className="btn-link" onClick={() => navigate('/teste-ia')}>← Voltar</button>
          <h1 style={{ margin: '8px 0 4px 0' }}>Teste #{run.id} {run.descricao && `— ${run.descricao}`}</h1>
          <small style={{ color: '#666' }}>
            {run.status} · {run.cases_count || cases.length} casos × {run.modelos.length} modelos ·
            {' '}criado por {run.criado_por_nome || '?'} em {new Date(run.created_at).toLocaleString('pt-BR')}
          </small>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13 }}>Tokens: <strong>{run.total_tokens?.toLocaleString('pt-BR') || 0}</strong></div>
          <div style={{ fontSize: 13 }}>Custo: <strong>${Number(run.custo_total_usd || 0).toFixed(4)}</strong></div>
          {run.status === 'em_execucao' && (
            <div style={{ fontSize: 13, color: '#1976d2' }}>⏳ {run.progresso_atual}/{run.progresso_total}</div>
          )}
        </div>
      </div>

      {/* Painel de stats por modelo */}
      {cases.length > 0 && (
        <div style={{ background: '#fafafa', padding: 12, borderRadius: 8, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>📊 Estatísticas por modelo</h3>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Modelo</th>
                <th style={{ padding: 6 }}>✅ Boas</th>
                <th style={{ padding: 6 }}>⚠️ Aceitáveis</th>
                <th style={{ padding: 6 }}>❌ Ruins</th>
                <th style={{ padding: 6 }}>Sem avaliação</th>
                <th style={{ padding: 6 }}>Erros</th>
                <th style={{ padding: 6 }}>Latência média</th>
                <th style={{ padding: 6 }}>Tokens</th>
                <th style={{ padding: 6 }}>Custo</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(statsModelo).map(([modelo, s]) => {
                const total = s.ok + s.aceitavel + s.ruim;
                const taxa = total > 0 ? ((s.ok / total) * 100).toFixed(0) : '-';
                return (
                  <tr key={modelo} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 6, fontWeight: 600 }}>{modelo} {taxa !== '-' && <small style={{ color: '#2e7d32' }}>{taxa}% ok</small>}</td>
                    <td style={{ padding: 6, textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{s.ok}</td>
                    <td style={{ padding: 6, textAlign: 'center', color: '#ed6c02', fontWeight: 600 }}>{s.aceitavel}</td>
                    <td style={{ padding: 6, textAlign: 'center', color: '#c62828', fontWeight: 600 }}>{s.ruim}</td>
                    <td style={{ padding: 6, textAlign: 'center', color: '#999' }}>{s.sem}</td>
                    <td style={{ padding: 6, textAlign: 'center', color: s.erros > 0 ? '#c62828' : '#999' }}>{s.erros}</td>
                    <td style={{ padding: 6, textAlign: 'center' }}>{s.n > 0 ? Math.round(s.latencia / s.n) + 'ms' : '-'}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{s.tokens.toLocaleString('pt-BR')}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>${s.custo.toFixed(4)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Lista de cases */}
      {cases.map((c, idx) => (
        <div key={c.id} style={{ border: '1px solid #ddd', borderRadius: 8, marginBottom: 24, overflow: 'hidden' }}>
          <div style={{ background: '#f5f5f5', padding: 12, borderBottom: '1px solid #ddd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <strong>Caso {idx + 1}/{cases.length}</strong> · {c.paciente_nome} <small style={{ color: '#666' }}>({c.telefone})</small>
                {c.chatwoot_conv_id && (
                  <small style={{ marginLeft: 8, color: '#1976d2' }}>conv chatwoot #{c.chatwoot_conv_id}</small>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {c.auto_id_resultado?.fonte === 'unico' && <>🎯 Auto-ID: {c.auto_id_resultado.nome} (ID {c.auto_id_resultado.id})</>}
                {c.auto_id_resultado?.fonte === 'multiplos' && <>👥 {c.auto_id_resultado.candidatos?.length} candidatos</>}
                {!c.auto_id_resultado && <>❌ Telefone não bateu com paciente</>}
              </div>
            </div>
            <div style={{ marginTop: 8, padding: 10, background: '#fff', borderRadius: 4 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Pergunta do paciente:</div>
              <div style={{ fontWeight: 600 }}>"{c.pergunta_paciente}"</div>
            </div>
            <div style={{ marginTop: 8, padding: 10, background: '#e8f5e9', borderRadius: 4 }}>
              <div style={{ fontSize: 12, color: '#388e3c', marginBottom: 2 }}>
                ▶ Resposta humana real ({c.resposta_humana_real?.autor || '?'}):
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.resposta_humana_real?.body || '(sem resposta)'}</div>
            </div>
          </div>

          {/* Respostas dos modelos */}
          <div style={{ padding: 12 }}>
            {(c.resultados || []).length === 0 ? (
              <p style={{ color: '#888' }}>⏳ Aguardando resultados dos modelos...</p>
            ) : (
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}>
                {c.resultados.map(r => (
                  <div key={r.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 10, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', paddingBottom: 6, marginBottom: 6 }}>
                      <div>
                        <strong style={{ fontSize: 13 }}>{r.modelo}</strong>
                        <div style={{ fontSize: 11, color: '#666' }}>
                          {r.iterations} iter · {r.latencia_ms}ms · {(r.tokens_input || 0)+(r.tokens_output || 0)} tok · ${Number(r.custo_usd || 0).toFixed(5)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {VEREDITOS.map(v => (
                          <button
                            key={v.v}
                            title={v.label}
                            onClick={() => setVeredito(r.id, r.veredito === v.v ? null : v.v)}
                            style={{
                              padding: '2px 8px', fontSize: 16, border: 'none', borderRadius: 4, cursor: 'pointer',
                              background: r.veredito === v.v ? v.color : '#eee',
                              opacity: r.veredito === v.v ? 1 : 0.5,
                            }}
                          >{v.emoji}</button>
                        ))}
                      </div>
                    </div>
                    {r.erro ? (
                      <div style={{ color: '#c62828', fontSize: 13 }}>❌ Erro: {r.erro}</div>
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, marginBottom: 6 }}>{r.resposta || <em>(sem resposta texto)</em>}</div>
                    )}
                    {renderTools(r.id, r.tools_chamadas)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      {cases.length === 0 && run.status === 'em_execucao' && (
        <p style={{ textAlign: 'center', padding: 30, color: '#888' }}>
          ⏳ Buscando conversas no Chatwoot... aguarde.
        </p>
      )}
    </div>
  );
}
